/**
 * 波B 产品树 → 自由创作(单帧海报)字段适配器。
 *
 * ═══ 为什么不能直接搬 `planProductFromNode` ═══════════════════════════════════
 * 那个是 **canvas 形状**(阶段一规划入参:`specs[{label,value,unit}]` + priceNow/priceOld
 * + complianceCategory,字段长度按 ai `PlanProduct` schema 截断)。自由创作这条路
 * 根本没有结构化产品字段 —— 它是 `FREEFORM_RAW`,`content.productName` 只是占位、
 * `sellingPoints` 故意留空(ai 侧对 FREEFORM_RAW 短路,不拼「产品: X 卖点: Y」骨架)。
 * 产品资料到达引擎的**唯一**通道是 `rawPrompt`,和 L1 画风 / L2 配色同一条。
 *
 * ═══ ★ 红线:一个参数都不许编 ═══════════════════════════════════════════════
 * 名称 / 卖点 / 参数 / 说明**逐字**取 `GET /tenant/products` 原文:
 *   · 不改写、不归纳、不补默认值 —— 编一个字,成图上就会出现租户没说过的话;
 *   · **也不截断**。这一条与 `planProductFromNode` 故意不同:那边截是因为 ai schema 有
 *     `max_length`,这边只有 `rawPrompt` 一个总长度闸门,而它是**拦下来告诉用户**的
 *     (`rawPromptOverflow`),不是偷偷剪掉。截断也是改写,只是用户看不见。
 *   · 产品库没有的字段(品牌 / 价格)一律不出现,不留空壳更不猜。
 * 可选性判据(`pickableProducts` / `isDisplayableAttribute`)与长图那条**共用同一份**,
 * 各写一遍必然漂 —— internal 节点的 attributes 里常放成本价,漏一次就画到客户看的图上。
 */
import type { ProductNode } from "@/entities/product";
import { isDisplayableAttribute, pickableProducts } from "../../longimage/model/productIntake";

/** 可当素材的产品节点(对客可见的在售叶子产品)—— 与长图规划入口同一个判据。 */
export { pickableProducts };

export interface FreeCreateProductSpec {
  label: string;
  value: string;
}

/** 自由创作用的产品快照(全部字段都是产品库原文)。 */
export interface FreeCreateProduct {
  code: string;
  name: string;
  /** 父节点原名;翻不到就没有这个字段(不猜类目)。 */
  category?: string;
  sellingPoints: string[];
  specs: FreeCreateProductSpec[];
  summary?: string;
  /**
   * 2026-08-01 —— 产品预置图的 **object key** 列表(存的是 key 不是 URL,URL 会过期)。
   * ★这个字段此前被适配器整个丢掉了,正是「卡面写着产品图锚定、页面从来不挂图」的物理原因。
   * ★只带 key:它不参与提示词,不受「一个字都不许编」那条红线约束,也不进 rawPrompt 预算。
   */
  sampleImages: string[];
}

export function freeCreateProductFromNode(
  node: ProductNode,
  allNodes: ProductNode[] = []
): FreeCreateProduct {
  const parentName = node.parent
    ? (allNodes.find((n) => n.code === node.parent)?.name ?? "").trim()
    : "";
  const specs: FreeCreateProductSpec[] = [];
  for (const [label, raw] of Object.entries(node.attributes ?? {})) {
    if (!isDisplayableAttribute(raw)) continue;
    const value = String(raw).trim();
    if (!label.trim() || !value) continue;
    specs.push({ label: label.trim(), value });
  }
  const summary = (node.summary ?? "").trim();
  return {
    code: node.code,
    name: node.name.trim(),
    ...(parentName ? { category: parentName } : {}),
    sellingPoints: (node.sellingPoints ?? []).map((s) => s.trim()).filter(Boolean),
    specs,
    ...(summary ? { summary } : {}),
    sampleImages: (node.sampleImages ?? []).filter((k) => typeof k === "string" && k.trim()),
  };
}

/**
 * 参数里表示「实物大小」的项(产品库 attributes 的键)。
 * ★它们不只是可印的参数,还是**画面比例约束**:不论上不上图,都要单独告诉模型按真实大小画
 *   (2026-10-19 中国珠宝:模型不懂「2cm」,试戴图里吊坠普遍画大一圈)。
 */
const SCALE_SPEC_LABELS = ["佩戴体感", "尺寸"];

function isScaleSpec(label: string): boolean {
  return SCALE_SPEC_LABELS.some((k) => label.startsWith(k));
}

/**
 * 用户原文里点名要「把参数印上图」的说法 —— 命中即视同勾了「在图上显示产品参数」。
 * ★只认这几个明确指向参数的词:宁可漏判(用户还能手动勾),不要误判(误判=参数表又被画上图)。
 */
const SPECS_ON_IMAGE_RE = /尺寸|参数|规格|克重|款号|详情/;

export function wantsProductSpecsOnImage(userPrompt: string): boolean {
  return SPECS_ON_IMAGE_RE.test(userPrompt);
}

/**
 * 拼进 `rawPrompt` 的【产品】段。
 *
 * 引导词(「产品:」「卖点:」…)是**固定字面量**,值一律原样 —— 这样"一个字都不许编"
 * 这条红线在提交侧也是可断言的(`buildRawPrompt.test.ts` 逐字比对)。
 * ★空字段整行不出现:写「卖点:无」是**编出来的话**,而且会被模型当成一条要表达的信息。
 *
 * ═══ 2026-10-19 参数默认不上图(老板拍板)═══════════════════════════════════════
 * 此前类目/参数/说明与卖点平铺在同一段,模型把它们当文案排成参数表画进画面
 * (演示机任务 6595870984194:提示词只写「模特试戴图,场景唯美」,成图右下角一整张
 * 款号/克重/尺寸表)。AI 优化路径早就把这三样标成「背景资料」(ai normalize/render.py),
 * 直出路径现在同口径:
 *   · 默认:类目/参数/说明进「背景资料」段,明说不写进画面;
 *   · `showSpecs`(用户勾选,或原文点名要尺寸/参数)= 旧行为,参数可以上图;
 *   · 尺寸类参数**两种情况都**再单独出一行「实物大小」比例约束 —— 上不上图是一回事,
 *     画多大是另一回事。
 */
export function productPromptSection(
  p: FreeCreateProduct,
  opts: { showSpecs?: boolean } = {}
): string {
  const lines = [`产品:${p.name}`];
  if (p.sellingPoints.length) lines.push(`卖点:${p.sellingPoints.join(";")}`);
  const scale = p.specs.filter((s) => isScaleSpec(s.label));
  if (opts.showSpecs) {
    if (p.category) lines.splice(1, 0, `类目:${p.category}`);
    if (p.specs.length) lines.push(`参数:${p.specs.map((s) => `${s.label} ${s.value}`).join(";")}`);
    if (p.summary) lines.push(`说明:${p.summary}`);
  } else {
    const bg: string[] = [];
    if (p.category) bg.push(`类目 ${p.category}`);
    const plain = p.specs.filter((s) => !isScaleSpec(s.label));
    if (plain.length) bg.push(plain.map((s) => `${s.label} ${s.value}`).join(";"));
    if (p.summary) bg.push(p.summary);
    if (bg.length) {
      lines.push(`背景资料(帮助理解产品外观与材质,不要作为文字写进画面):${bg.join(";")}`);
    }
  }
  // ★不上图时只发**不带数字**的体感项(「佩戴体感」):带毫米数的「尺寸」一发,模型照样
  //   排成「大版 宽约2.4cm…」的版本条(演示机 1019 验证 3 张里 1 张)。没有体感项才退回尺寸。
  const feel = scale.filter((s) => s.label.startsWith("佩戴体感"));
  const scaleOut = opts.showSpecs ? scale : feel.length ? feel : scale;
  if (scaleOut.length) {
    const guide = opts.showSpecs
      ? "按真实大小画产品,戴在人身上时不要放大"
      : "只用于把握比例:按真实大小画产品,戴在人身上时不要放大,不要写进画面";
    lines.push(`实物大小(${guide}):${scaleOut.map((s) => s.value).join(";")}`);
  }
  return lines.join("\n");
}

/**
 * 草稿回填前验型 —— 形状不对**整包丢弃**。
 * (与 `parseStyleTokens` 同口径:陈年 formJson 里半份脏数据比没有更危险,
 *  它会一路进出图指令,而界面上看起来像"库里就是这么写的"。)
 */
export function parseFreeCreateProduct(raw: unknown): FreeCreateProduct | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.code !== "string" || !r.code) return null;
  if (typeof r.name !== "string" || !r.name) return null;
  if (!Array.isArray(r.sellingPoints) || r.sellingPoints.some((s) => typeof s !== "string")) {
    return null;
  }
  if (!Array.isArray(r.specs)) return null;
  const specs: FreeCreateProductSpec[] = [];
  for (const s of r.specs) {
    if (!s || typeof s !== "object") return null;
    const { label, value } = s as Record<string, unknown>;
    if (typeof label !== "string" || typeof value !== "string") return null;
    specs.push({ label, value });
  }
  if (r.category !== undefined && typeof r.category !== "string") return null;
  if (r.summary !== undefined && typeof r.summary !== "string") return null;
  // ★sampleImages 必须一起收回来:草稿是 2026-08-01 之前存的话这个键根本不存在 ——
  //   缺键按空数组兜底(=没有锚,段控不出现),而**不是**整包丢弃:老草稿的产品资料仍然有效。
  //   丢了它的后果是「恢复出来的草稿看起来一样,但产品图锚定静默失效」。
  if (r.sampleImages !== undefined) {
    if (!Array.isArray(r.sampleImages) || r.sampleImages.some((k) => typeof k !== "string")) {
      return null;
    }
  }
  return {
    code: r.code,
    name: r.name,
    ...(r.category ? { category: r.category as string } : {}),
    sellingPoints: r.sellingPoints as string[],
    specs,
    ...(r.summary ? { summary: r.summary as string } : {}),
    sampleImages: ((r.sampleImages as string[]) ?? []).filter((k) => k.trim()),
  };
}
